import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '../../../components/layout/AppLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { ArrowLeft, MapPin, Calendar, Eye, MessageCircle, TrendingUp, AlertCircle, CheckCircle, Truck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  'delivery_transport': 'Delivery & Transport',
  'home_services': 'Home Services',
  'errands_tasks': 'Errands & Tasks',
  'shopping_assistance': 'Shopping Assistance',
  'professional_services': 'Professional Services',
  'tutoring_education': 'Tutoring & Education',
  'events_entertainment': 'Events & Entertainment',
  'tech_support': 'Tech Support',
  'health_wellness': 'Health & Wellness',
  'items_goods': 'Items & Goods',
  'other': 'Other'
};

export const WantOfferDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    loadPost();
    loadMatches();
  }, [id]);

  const loadPost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wants-offers/${id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(data.post);
        
        // Check if current user is owner
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setIsOwner(user.id === data.post.user_id);
        }
      } else {
        toast.error('Post not found');
        navigate('/wants-offers');
      }
    } catch (error) {
      console.error('Failed to load post:', error);
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wants-offers/${id}/matches`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches || []);
      }
    } catch (error) {
      console.log('Failed to load matches (may not be owner)');
    }
  };

  const handleStartConversation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          participant_ids: [post.user_id],
          type: 'dm'
        })
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/user/conversations`);
        toast.success('Conversation started!');
      }
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const handleConvertToJob = async () => {
    if (post.post_type !== 'want') {
      toast.error('Only Wants can be converted to jobs');
      return;
    }

    try {
      setConverting(true);
      const response = await fetch(`${API_URL}/api/wants-offers/${id}/convert-to-job`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Navigate to Runners with pre-filled data
        navigate('/pickup/post', { state: { jobData: data.job_data } });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to convert to job');
      }
    } catch (error) {
      toast.error('Failed to convert to job');
    } finally {
      setConverting(false);
    }
  };

  const handleClosePost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wants-offers/${id}/close`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Post closed successfully');
        loadPost();
      }
    } catch (error) {
      toast.error('Failed to close post');
    }
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wants-offers')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Badge variant={post.post_type === 'want' ? 'default' : 'secondary'}>
            {post.post_type === 'want' ? 'WANT' : 'OFFER'}
          </Badge>
        </div>

        {/* Main Post Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-2xl mb-2">{post.title}</CardTitle>
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {post.location_city}, {post.location_country}
                    {post.location_area && ` (${post.location_area})`}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Posted {formatDate(post.created_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {post.views} views
                  </div>
                </div>
              </div>
              {post.budget && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    {post.currency} {post.budget.toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {post.post_type === 'want' ? 'Budget' : 'Asking price'}
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Description */}
            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{post.description}</p>
            </div>

            {/* Category */}
            <div>
              <h3 className="font-semibold mb-2">Category</h3>
              <Badge variant="outline">{CATEGORIES[post.category] || post.category}</Badge>
            </div>

            {/* Posted By */}
            <div>
              <h3 className="font-semibold mb-2">Posted by</h3>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                  {post.display_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium">{post.display_name}</div>
                  <div className="text-sm text-muted-foreground">@{post.username}</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              {!isOwner && post.status === 'active' && (
                <Button onClick={handleStartConversation} className="gap-2">
                  <MessageCircle className="h-4 w-4" />
                  Contact
                </Button>
              )}
              
              {isOwner && post.status === 'active' && (
                <>
                  {post.post_type === 'want' && (
                    <Button
                      onClick={handleConvertToJob}
                      disabled={converting}
                      variant="outline"
                      className="gap-2"
                    >
                      <Truck className="h-4 w-4" />
                      {converting ? 'Converting...' : 'Convert to Runners Job'}
                    </Button>
                  )}
                  <Button onClick={() => navigate(`/wants-offers/${id}/edit`)} variant="outline">
                    Edit Post
                  </Button>
                  <Button onClick={handleClosePost} variant="outline">
                    Close Post
                  </Button>
                </>
              )}
              
              {post.status === 'closed' && (
                <Badge variant="secondary" className="gap-2">
                  <CheckCircle className="h-4 w-4" />
                  This post is closed
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Matches Section (Owner Only) */}
        {isOwner && matches.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Potential Matches ({matches.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {matches.slice(0, 5).map((match) => (
                  <div
                    key={match.id}
                    className="flex items-start justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer"
                    onClick={() => navigate(`/wants-offers/${match.id}`)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant={match.post_type === 'want' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {match.post_type.toUpperCase()}
                        </Badge>
                        <span className="text-sm font-semibold text-green-600">
                          {match.match_score}% match
                        </span>
                      </div>
                      <div className="font-medium">{match.title}</div>
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {match.description}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {match.location_city} • by {match.display_name}
                      </div>
                    </div>
                    {match.budget && (
                      <div className="text-right ml-4">
                        <div className="font-semibold text-green-600">
                          {match.currency} {match.budget.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Report Section (Non-Owner) */}
        {!isOwner && (
          <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Report this post</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    If this post violates our guidelines or seems suspicious, you can report it.
                  </p>
                  <Button variant="outline" size="sm">
                    Report Post
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};
